import { Box } from '@mui/material';
import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useAccountantTsStatusesColumns } from '../hooks/useAccountantTsStatusesColumns';
import { FilterSection } from './FilterSection';
import { SubmitterTabs } from './SubmitterTabs';
import { useUsersWithTsPeriodData } from '../../../hooks/useUsersWithTsPeriodData';
import { UserWithTsPeriodData } from '../../../types';
import { TimesheetStatusesTableContent } from './TimesheetStatusesTableContent';
import { useFilteredOpsTimesheets } from '../../../queries/hooks/useFilteredOpsTimesheets';
import { useAtomValue } from 'jotai';
import {
  tsStatusesRangeEnd,
  tsStatusesRangeStart,
  tsStatusesSubmitterLabel,
  tsStatusesShowInactive,
} from '../../../state/UIAccountantState';

interface AccontantTimesheetStatusesTableProps {
  onTableReady?: (table: any) => void;
}

export function AccontantTimesheetStatusesTable({
  onTableReady,
}: AccontantTimesheetStatusesTableProps) {

  const rangeStart = useAtomValue(tsStatusesRangeStart);
  const rangeEnd = useAtomValue(tsStatusesRangeEnd);
  const showInactive = useAtomValue(tsStatusesShowInactive);
  const submitterLabel = useAtomValue(tsStatusesSubmitterLabel);

  // Fetch every status and filter here, so the "Show inactive" toggle can show its count.
  const { isLoading: timesheetsLoading, data: filteredOperatingPartners } =
    useFilteredOpsTimesheets(rangeStart, rangeEnd, undefined, submitterLabel);

  const visibleOperatingPartners = useMemo(
    () =>
      filteredOperatingPartners
        ?.filter((op) => showInactive || op.isActive !== false)
        .sort((a, b) => Number(a.isActive === false) - Number(b.isActive === false)),
    [filteredOperatingPartners, showInactive]
  );

  const inactiveIds = useMemo(
    () => new Set((visibleOperatingPartners ?? []).filter((op) => op.isActive === false).map((op) => op.id)),
    [visibleOperatingPartners]
  );

  const operatingPartnersWithMappedPeriods = useUsersWithTsPeriodData(visibleOperatingPartners);
  const columnsMappedPeriods = useAccountantTsStatusesColumns(visibleOperatingPartners || []);

  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const createTableConfig = <T,>(data: T[], columns: any[], includeRowSelection = false) => ({
    data,
    columns,
    state: {
      ...(includeRowSelection && { rowSelection }),
      sorting,
      columnFilters,
    },
    ...(includeRowSelection && { onRowSelectionChange: setRowSelection }),
    enableRowSelection: includeRowSelection,
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel<T>(),
    getSortedRowModel: getSortedRowModel<T>(),
    getFilteredRowModel: getFilteredRowModel<T>(),
    getFacetedUniqueValues: getFacetedUniqueValues<T>(),
    ...(includeRowSelection && { getRowId: (item: T) => String((item as any).id) }),
  });

  const table = useReactTable(
    createTableConfig<UserWithTsPeriodData>(
      (operatingPartnersWithMappedPeriods as UserWithTsPeriodData[]) ?? [],
      columnsMappedPeriods,
      true
    )
  );

  useEffect(() => {
    if (onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  return (
    <div>
      <SubmitterTabs table={table} />
      <Box display={'flex'} justifyContent={'space-between'} flexWrap={'wrap'}>
        <FilterSection table={table} columnFilters={columnFilters} />
      </Box>
      <TimesheetStatusesTableContent
        table={table}
        timesheetsLoading={timesheetsLoading || !filteredOperatingPartners}
        getRowClassName={(row) => (inactiveIds.has(row.original.id) ? 'muted-row' : undefined)}
      />
    </div>
  );
}
